/**
 * Fixed handle configurations for switchboard nodes
 * Defines anchor positions and labels for fixed handles
 */

/**
 * Anchor positions for fixed handles
 */
export const FIXED_HANDLE_ANCHORS = {
  schedule: {
    whenOpen: 'TopRight' as const,
    whenClosed: 'Right' as const,
    holiday: 'BottomRight' as const,
  },
  day_night: {
    whenOpen: 'TopRight' as const,
    whenClosed: 'BottomRight' as const,
  },
  queue: {
    onTimeout: 'TopRight' as const,
    onEscape: 'BottomRight' as const,
  },
  transfer_rollover: {
    noAnswer: 'TopRight' as const,
    busy: 'BottomRight' as const,
  },
  auto_attendant: {
    onTimeout: 'TopRight' as const,
    onInvalid: 'TopLeft' as const, // Legacy uses TopLeft, NOT BottomRight
  },
  interactive_webhook: {
    onTimeout: 'TopRight' as const,
    onInvalid: 'TopLeft' as const, // Legacy uses TopLeft, NOT BottomRight
  },
  ai_agent_v1: {
    onError: 'Right' as const,
  },
} as const

/**
 * Labels for fixed handles
 * CRITICAL: Schedule uses "Open" and "Closed" (NOT "When Open" and "When Closed")
 */
export const FIXED_HANDLE_LABELS = {
  schedule: {
    whenOpen: 'Open' as const, // NOT "When Open"
    whenClosed: 'Closed' as const, // NOT "When Closed"
    holiday: 'Holiday' as const,
  },
  day_night: {
    whenOpen: 'When Open' as const,
    whenClosed: 'When Closed' as const,
  },
  queue: {
    onTimeout: 'On Timeout' as const,
    onEscape: 'On Escape' as const,
  },
  transfer_rollover: {
    noAnswer: 'No Answer' as const,
    busy: 'Busy' as const,
  },
  auto_attendant: {
    onTimeout: 'On Timeout' as const,
    onInvalid: 'On Invalid' as const,
  },
  interactive_webhook: {
    onTimeout: 'On Timeout' as const,
    onInvalid: 'On Invalid' as const,
  },
  ai_agent_v1: {
    onError: 'On Error' as const,
  },
} as const

/**
 * Default labels for standard handles
 */
export const DEFAULT_LABELS = {
  source: 'Drag' as const,
  target: 'Drop' as const,
} as const

/**
 * Default anchor positions
 */
export const DEFAULT_ANCHORS = {
  source: 'Right' as const,
  target: 'Left' as const,
} as const

/**
 * Fixed handle configurations per node type
 */
export interface FixedHandleConfig {
  handleName: string
  anchor: 'TopRight' | 'Right' | 'BottomRight'
  label: string
}

export const FIXED_HANDLE_CONFIGS: Record<
  'schedule' | 'day_night' | 'queue' | 'transfer_rollover',
  FixedHandleConfig[]
> = {
  schedule: [
    { handleName: 'whenOpen', anchor: 'TopRight', label: 'Open' },
    { handleName: 'whenClosed', anchor: 'Right', label: 'Closed' },
    { handleName: 'holiday', anchor: 'BottomRight', label: 'Holiday' },
  ],
  day_night: [
    { handleName: 'whenOpen', anchor: 'TopRight', label: 'When Open' },
    { handleName: 'whenClosed', anchor: 'BottomRight', label: 'When Closed' },
  ],
  queue: [
    { handleName: 'onTimeout', anchor: 'TopRight', label: 'On Timeout' },
    { handleName: 'onEscape', anchor: 'BottomRight', label: 'On Escape' },
  ],
  transfer_rollover: [
    { handleName: 'noAnswer', anchor: 'TopRight', label: 'No Answer' },
    { handleName: 'busy', anchor: 'BottomRight', label: 'Busy' },
  ],
} as const
